import type { ReactNode } from 'react';

import {
    Card,
    CardHeader,
    Dropdown,
    Field,
    Input,
    StatusPill,
    Table,
    TableBody,
    TableCell,
    TableFootNote,
    TableHead,
    TableHeaderCell,
    TableRow,
    Toggle,
} from '@/design-system';
import {
    AUDIT_LOG_NAME_LABELS,
    AUDIT_LOG_NAMES,
    auditLogNameLabel,
    type AuditLogEntry,
    type AuditLogFilters,
    type PlatformTenant,
} from '@/entities/tenant';
import { formatDayTimeShort } from '@/shared/lib';

import { adminCopy } from '../copy';

export interface AuditLogCardProps {
    entries: readonly AuditLogEntry[];
    filters: AuditLogFilters;
    onFiltersChange: (filters: AuditLogFilters) => void;
    tenants: readonly PlatformTenant[];
    /** Op het detail van een organisatie staat die al vast. */
    lockedTenant?: boolean;
    footer?: ReactNode;
}

/**
 * Audit log: wie deed wat, bij welke organisatie, en waarom.
 *
 * Elke handmatige boeking en elke meekijksessie komt hier terecht met de
 * verplichte reden erbij. Ook dit is een logboek: geen bewerkknoppen, alleen
 * filteren.
 */
export function AuditLogCard({
    entries,
    filters,
    onFiltersChange,
    tenants,
    lockedTenant = false,
    footer,
}: AuditLogCardProps) {
    const tenantName = (uuid: string | null) =>
        uuid === null
            ? adminCopy.auditPlatform
            : (tenants.find((tenant) => tenant.uuid === uuid)?.name ?? adminCopy.unknown);

    return (
        <Card clip as="section" aria-labelledby="audit-title">
            <CardHeader
                title={<span id="audit-title">{adminCopy.auditTitle}</span>}
                description={adminCopy.auditIntro}
            />

            <div className="flex flex-wrap items-end gap-2 border-b border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-3">
                {lockedTenant ? null : (
                    <Field label={adminCopy.auditTenant} size="sm" className="min-w-[180px]">
                        <Dropdown
                            value={filters.tenantUuid ?? ''}
                            options={[
                                { value: '', label: adminCopy.auditTenantAll },
                                ...tenants.map((tenant) => ({
                                    value: tenant.uuid,
                                    label: tenant.name,
                                })),
                            ]}
                            onChange={(tenantUuid) =>
                                onFiltersChange({ ...filters, tenantUuid, page: 1 })
                            }
                        />
                    </Field>
                )}

                <Field label={adminCopy.auditType} size="sm" className="min-w-[160px]">
                    <Dropdown
                        value={filters.logName ?? ''}
                        options={[
                            { value: '', label: adminCopy.auditTypeAll },
                            ...AUDIT_LOG_NAMES.map((name) => ({
                                value: name,
                                label: AUDIT_LOG_NAME_LABELS[name],
                            })),
                        ]}
                        onChange={(logName) => onFiltersChange({ ...filters, logName, page: 1 })}
                    />
                </Field>

                <Field label={adminCopy.auditFrom} size="sm" className="min-w-[136px]">
                    <Input
                        type="date"
                        value={filters.from ?? ''}
                        onChange={(event) =>
                            onFiltersChange({ ...filters, from: event.target.value, page: 1 })
                        }
                    />
                </Field>

                <Field label={adminCopy.auditUntil} size="sm" className="min-w-[136px]">
                    <Input
                        type="date"
                        value={filters.until ?? ''}
                        onChange={(event) =>
                            onFiltersChange({ ...filters, until: event.target.value, page: 1 })
                        }
                    />
                </Field>

                <div className="flex min-h-[40px] items-center">
                    <Toggle
                        checked={filters.impersonatedOnly ?? false}
                        label={adminCopy.auditImpersonatedOnly}
                        onCheckedChange={(checked) =>
                            onFiltersChange({ ...filters, impersonatedOnly: checked, page: 1 })
                        }
                    />
                </div>
            </div>

            <Table
                columns="124px 120px 150px minmax(0,1fr) 140px"
                minWidth={840}
                label={adminCopy.auditTitle}
            >
                <TableHead>
                    <TableHeaderCell>{adminCopy.columnWhen}</TableHeaderCell>
                    <TableHeaderCell>{adminCopy.columnType}</TableHeaderCell>
                    <TableHeaderCell>{adminCopy.columnTenant}</TableHeaderCell>
                    <TableHeaderCell>{adminCopy.columnWhat}</TableHeaderCell>
                    <TableHeaderCell>{adminCopy.columnBy}</TableHeaderCell>
                </TableHead>
                <TableBody>
                    {entries.map((entry, index) => (
                        <TableRow key={entry.uuid ?? `${entry.createdAt}-${index}`}>
                            <TableCell mono muted>
                                {entry.createdAt
                                    ? formatDayTimeShort(entry.createdAt)
                                    : adminCopy.unknown}
                            </TableCell>
                            <TableCell className="py-[9px]">
                                <StatusPill tone="neutral" size="sm">
                                    {auditLogNameLabel(entry.logName)}
                                </StatusPill>
                            </TableCell>
                            <TableCell muted truncate>
                                {tenantName(entry.tenantUuid)}
                            </TableCell>
                            <TableCell>
                                <span className="block text-[var(--text-primary)]">
                                    {entry.description}
                                </span>
                                {entry.reason ? (
                                    <span className="block text-[12px] text-[var(--text-tertiary-safe)]">
                                        {adminCopy.auditReason(entry.reason)}
                                    </span>
                                ) : null}
                            </TableCell>
                            <TableCell muted truncate>
                                {entry.actorName ?? adminCopy.auditSystem}
                                {entry.duringImpersonation ? (
                                    <span className="block text-[11px] text-[var(--warn-text)]">
                                        {adminCopy.auditDuringImpersonation}
                                    </span>
                                ) : null}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {entries.length === 0 ? <TableFootNote>{adminCopy.auditEmpty}</TableFootNote> : null}
            {footer}
        </Card>
    );
}
